import { ResultRow, animalNames, animalEmojis } from "./constants";

export type Modalidade =
  | "grupo"
  | "dezena"
  | "centena"
  | "milhar"
  | "duque_dezena"
  | "terno_dezena"
  | "duque_grupo"
  | "terno_grupo";

export const MODALIDADES: { value: Modalidade; label: string }[] = [
  { value: "grupo", label: "Grupos" },
  { value: "dezena", label: "Dezenas" },
  { value: "centena", label: "Centenas" },
  { value: "milhar", label: "Milhares" },
  { value: "duque_dezena", label: "Duque de dezena" },
  { value: "terno_dezena", label: "Terno de dezena" },
  { value: "duque_grupo", label: "Duque de grupo" },
  { value: "terno_grupo", label: "Terno de grupo" },
];

export const DIAS_SEMANA = [
  { value: 0, label: "Dom" },
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sáb" },
];

export const FAIXAS_PREMIO: { value: string; label: string; posicoes: number[] }[] = [
  { value: "1", label: "Só 1º prêmio", posicoes: [1] },
  { value: "1-3", label: "1º ao 3º", posicoes: [1, 2, 3] },
  { value: "1-5", label: "1º ao 5º", posicoes: [1, 2, 3, 4, 5] },
  { value: "1-7", label: "1º ao 7º", posicoes: [1, 2, 3, 4, 5, 6, 7] },
  { value: "custom", label: "Escolher prêmios", posicoes: [] },
];

export interface AdvancedFilters {
  fontes: string[]; // 'rio' | 'federal'
  posicoes: number[];
  diasSemana: number[];
  turnos: string[];
  modalidades: Modalidade[];
}

export interface RankItem {
  key: string;
  label: string;
  freq: number;
  pct: number;
  atraso: number;
  maiorAtraso: number;
  ultimaData: string | null;
  ultimoTurno: string | null;
  tendencia: number; // freq relativa recente vs média (>1 = em alta)
}

export interface Sorteio {
  id: string;
  data: string;
  turno: string;
  fonte: string;
  diaSemana: number;
  rows: ResultRow[];
}

const parseDia = (data: string): number => {
  const [y, m, d] = data.split("-").map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1)).getUTCDay();
};

export const dezenaDe = (milhar: string) => milhar.slice(-2).padStart(2, "0");
export const centenaDe = (milhar: string) => milhar.slice(-3).padStart(3, "0");
export const grupoDeDezena = (dz: string) => {
  const n = parseInt(dz, 10);
  return n === 0 ? 25 : Math.floor((n - 1) / 4) + 1;
};

export const nomeGrupo = (g: number) =>
  `${animalEmojis[g] || ""} G${String(g).padStart(2, "0")} ${animalNames[g] || ""}`.trim();

/** Agrupa as linhas em sorteios (data + turno + fonte) já aplicando os filtros avançados. */
export function buildSorteios(rows: ResultRow[], f: AdvancedFilters): Sorteio[] {
  const map = new Map<string, Sorteio>();
  for (const r of rows) {
    const fonte = (r as any).fonte || (r.turno === "FED" ? "federal" : "rio");
    if (f.fontes.length && !f.fontes.includes(fonte)) continue;
    if (f.posicoes.length && !f.posicoes.includes(r.posicao)) continue;
    if (f.turnos.length && fonte === "rio" && !f.turnos.includes(r.turno)) continue;
    const dia = parseDia(r.data);
    if (f.diasSemana.length && !f.diasSemana.includes(dia)) continue;

    const id = `${r.data}|${r.turno}|${fonte}`;
    let s = map.get(id);
    if (!s) {
      s = { id, data: r.data, turno: r.turno, fonte, diaSemana: dia, rows: [] };
      map.set(id, s);
    }
    s.rows.push(r);
  }
  // mais recente primeiro
  return Array.from(map.values()).sort((a, b) =>
    a.data === b.data ? (a.turno < b.turno ? 1 : -1) : a.data < b.data ? 1 : -1
  );
}

const combinacoes = (arr: string[], size: number): string[][] => {
  const out: string[][] = [];
  const rec = (start: number, acc: string[]) => {
    if (acc.length === size) {
      out.push([...acc]);
      return;
    }
    for (let i = start; i < arr.length; i++) rec(i + 1, [...acc, arr[i]]);
  };
  rec(0, []);
  return out;
};

/** Itens (chaves) sorteados em um sorteio para a modalidade escolhida. */
export function itensDoSorteio(s: Sorteio, mod: Modalidade): string[] {
  const milhares = s.rows.map((r) => r.milhar.padStart(4, "0"));
  switch (mod) {
    case "milhar":
      return Array.from(new Set(milhares));
    case "centena":
      return Array.from(new Set(milhares.map(centenaDe)));
    case "dezena":
      return Array.from(new Set(milhares.map(dezenaDe)));
    case "grupo":
      return Array.from(new Set(milhares.map((m) => String(grupoDeDezena(dezenaDe(m))))));
    case "duque_dezena":
      return combinacoes(Array.from(new Set(milhares.map(dezenaDe))).sort(), 2).map((c) => c.join("-"));
    case "terno_dezena":
      return combinacoes(Array.from(new Set(milhares.map(dezenaDe))).sort(), 3).map((c) => c.join("-"));
    case "duque_grupo":
      return combinacoes(
        Array.from(new Set(milhares.map((m) => String(grupoDeDezena(dezenaDe(m)))))).sort(
          (a, b) => Number(a) - Number(b)
        ),
        2
      ).map((c) => c.join("-"));
    case "terno_grupo":
      return combinacoes(
        Array.from(new Set(milhares.map((m) => String(grupoDeDezena(dezenaDe(m)))))).sort(
          (a, b) => Number(a) - Number(b)
        ),
        3
      ).map((c) => c.join("-"));
    default:
      return [];
  }
}

export function rotuloItem(mod: Modalidade, key: string): string {
  if (mod === "grupo") return nomeGrupo(Number(key));
  if (mod === "duque_grupo" || mod === "terno_grupo")
    return key
      .split("-")
      .map((g) => nomeGrupo(Number(g)))
      .join("  +  ");
  if (mod === "duque_dezena" || mod === "terno_dezena") return key.split("-").join(" + ");
  return key;
}

/** Universo total de itens possíveis (para listar os "nunca sorteados"). */
export function universo(mod: Modalidade): string[] | null {
  if (mod === "grupo") return Array.from({ length: 25 }, (_, i) => String(i + 1));
  if (mod === "dezena") return Array.from({ length: 100 }, (_, i) => String(i).padStart(2, "0"));
  if (mod === "centena") return Array.from({ length: 1000 }, (_, i) => String(i).padStart(3, "0"));
  return null; // milhar e combinações: universo grande demais para listar
}

export interface RankResult {
  total: number;
  totalSorteios: number;
  itens: RankItem[];
  nuncaSorteados: string[];
}

export function analisar(sorteios: Sorteio[], mod: Modalidade): RankResult {
  const freq = new Map<string, number>();
  const ultimoIdx = new Map<string, number>();
  const maiorAtraso = new Map<string, number>();
  const anteriorIdx = new Map<string, number>();
  const ultimaData = new Map<string, string>();
  const ultimoTurno = new Map<string, string>();
  const recente = new Map<string, number>();
  const JANELA = Math.min(20, sorteios.length);

  let total = 0;
  sorteios.forEach((s, idx) => {
    const itens = itensDoSorteio(s, mod);
    for (const it of itens) {
      freq.set(it, (freq.get(it) || 0) + 1);
      total++;
      if (!ultimoIdx.has(it)) {
        ultimoIdx.set(it, idx);
        ultimaData.set(it, s.data);
        ultimoTurno.set(it, s.turno);
      }
      const prev = anteriorIdx.get(it);
      if (prev !== undefined) {
        const gap = idx - prev - 1;
        if (gap > (maiorAtraso.get(it) || 0)) maiorAtraso.set(it, gap);
      }
      anteriorIdx.set(it, idx);
      if (idx < JANELA) recente.set(it, (recente.get(it) || 0) + 1);
    }
  });

  const n = sorteios.length;
  const itens: RankItem[] = Array.from(freq.entries()).map(([key, f]) => {
    const atraso = ultimoIdx.get(key) ?? n;
    const mAtraso = Math.max(maiorAtraso.get(key) || 0, atraso);
    const esperadoRecente = JANELA > 0 && n > 0 ? (f * JANELA) / n : 0;
    const tendencia = esperadoRecente > 0 ? (recente.get(key) || 0) / esperadoRecente : 0;
    return {
      key,
      label: rotuloItem(mod, key),
      freq: f,
      pct: total > 0 ? (f / total) * 100 : 0,
      atraso,
      maiorAtraso: mAtraso,
      ultimaData: ultimaData.get(key) || null,
      ultimoTurno: ultimoTurno.get(key) || null,
      tendencia,
    };
  });

  const uni = universo(mod);
  const nuncaSorteados = uni ? uni.filter((k) => !freq.has(k)) : [];

  return { total, totalSorteios: n, itens, nuncaSorteados };
}

export type Ordem = "mais" | "menos" | "atraso" | "alta";

export function ordenar(itens: RankItem[], ordem: Ordem): RankItem[] {
  const arr = [...itens];
  switch (ordem) {
    case "mais":
      return arr.sort((a, b) => b.freq - a.freq || a.atraso - b.atraso);
    case "menos":
      return arr.sort((a, b) => a.freq - b.freq || b.atraso - a.atraso);
    case "atraso":
      return arr.sort((a, b) => b.atraso - a.atraso || b.freq - a.freq);
    case "alta":
      return arr.sort((a, b) => b.tendencia - a.tendencia || b.freq - a.freq);
    default:
      return arr;
  }
}

export interface Insight {
  titulo: string;
  texto: string;
}

const fmtData = (d: string | null) => {
  if (!d) return "-";
  const [y, m, dd] = d.split("-");
  return `${dd}/${m}/${y.slice(2)}`;
};

export function gerarInsights(sorteios: Sorteio[]): Insight[] {
  if (sorteios.length === 0) return [];
  const out: Insight[] = [];

  const grupos = analisar(sorteios, "grupo");
  const dezenas = analisar(sorteios, "dezena");
  const duques = analisar(sorteios, "duque_dezena");

  const gTop = ordenar(grupos.itens, "mais")[0];
  if (gTop)
    out.push({
      titulo: "Grupo dominante",
      texto: `${gTop.label} apareceu ${gTop.freq}x (${gTop.pct.toFixed(1)}% das saídas analisadas). Última vez em ${fmtData(gTop.ultimaData)}.`,
    });

  const gAtraso = ordenar(grupos.itens, "atraso")[0];
  if (gAtraso)
    out.push({
      titulo: "Maior atraso",
      texto: `${gAtraso.label} está há ${gAtraso.atraso} sorteios sem sair (recorde de atraso: ${gAtraso.maiorAtraso}).`,
    });

  const dAlta = ordenar(dezenas.itens.filter((i) => i.freq >= 3), "alta")[0];
  if (dAlta)
    out.push({
      titulo: "Dezena em alta",
      texto: `A dezena ${dAlta.label} saiu ${(dAlta.tendencia).toFixed(1)}x acima da própria média nos últimos sorteios.`,
    });

  const duqueTop = ordenar(duques.itens, "mais")[0];
  if (duqueTop && duqueTop.freq > 1)
    out.push({
      titulo: "Duque mais repetido",
      texto: `As dezenas ${duqueTop.label} saíram juntas no mesmo sorteio ${duqueTop.freq}x.`,
    });

  // Dia da semana mais favorável ao grupo líder
  if (gTop) {
    const porDia = new Map<number, number>();
    for (const s of sorteios) {
      if (itensDoSorteio(s, "grupo").includes(gTop.key))
        porDia.set(s.diaSemana, (porDia.get(s.diaSemana) || 0) + 1);
    }
    const melhor = Array.from(porDia.entries()).sort((a, b) => b[1] - a[1])[0];
    if (melhor)
      out.push({
        titulo: "Dia mais favorável",
        texto: `${gTop.label} sai com mais frequência em ${DIAS_SEMANA.find((d) => d.value === melhor[0])?.label || "?"} (${melhor[1]} vezes).`,
      });
  }

  const frio = ordenar(dezenas.itens, "menos")[0];
  if (frio)
    out.push({
      titulo: "Dezena mais fria",
      texto: `A dezena ${frio.label} só apareceu ${frio.freq}x em ${sorteios.length} sorteios analisados.`,
    });

  return out;
}

/* ==========================================================
   Raio-X do Bicho — análise focada em um grupo (animal)
   ========================================================== */

export const dezenasDoGrupo = (g: number): string[] => {
  if (g === 25) return ["97", "98", "99", "00"];
  const base = (g - 1) * 4 + 1;
  return [base, base + 1, base + 2, base + 3].map((n) => String(n).padStart(2, "0"));
};

export interface RaioXBichoResult {
  grupo: number;
  totalSorteios: number;
  resumo: RankItem | null;
  dezenas: RankItem[];
  centenas: RankItem[];
  milhares: RankItem[];
  dezenaForte: RankItem | null;
  centenaProvavel: RankItem | null;
  milharProvavel: { valor: string; freq: number; combinada: boolean } | null;
}

export function analisarBicho(sorteios: Sorteio[], grupo: number): RaioXBichoResult {
  const dz = dezenasDoGrupo(grupo);
  const pertence = (fim2: string) => dz.includes(fim2);

  const gRes = analisar(sorteios, "grupo");
  const dRes = analisar(sorteios, "dezena");
  const cRes = analisar(sorteios, "centena");
  const mRes = analisar(sorteios, "milhar");

  const resumo = gRes.itens.find((i) => i.key === String(grupo)) || null;
  const dezenas = ordenar(dRes.itens.filter((i) => pertence(i.key)), "mais");
  const centenas = ordenar(cRes.itens.filter((i) => pertence(i.key.slice(-2))), "mais");
  const milhares = ordenar(mRes.itens.filter((i) => pertence(i.key.slice(-2))), "mais");

  const dezenaForte = dezenas[0] || null;
  const centenaProvavel = centenas[0] || null;

  let milharProvavel: RaioXBichoResult["milharProvavel"] = null;
  if (milhares[0] && milhares[0].freq > 1) {
    milharProvavel = { valor: milhares[0].key, freq: milhares[0].freq, combinada: false };
  } else if (centenaProvavel) {
    // monta a milhar combinando a centena líder com o dígito de milhar mais frequente do grupo
    const cont = new Map<string, number>();
    for (const m of milhares) {
      const d = m.key.padStart(4, "0")[0];
      cont.set(d, (cont.get(d) || 0) + m.freq);
    }
    const topDigito = Array.from(cont.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || "0";
    milharProvavel = {
      valor: `${topDigito}${centenaProvavel.key}`,
      freq: milhares[0]?.freq || 0,
      combinada: true,
    };
  }

  return {
    grupo,
    totalSorteios: sorteios.length,
    resumo,
    dezenas,
    centenas,
    milhares,
    dezenaForte,
    centenaProvavel,
    milharProvavel,
  };
}
